import { createURL } from './urlFormatting';

/** JW Broadcast Live events: minimum 5 minutes, maximum 24 hours. */
export const BROADCAST_LIVE_EVENT_MIN_DURATION_MS = 5 * 60 * 1000;
export const BROADCAST_LIVE_EVENT_MAX_DURATION_MS = 24 * 60 * 60 * 1000;
export const BROADCAST_LIVE_MIN_START_AHEAD_MS = 5 * 60 * 1000;

export type BroadcastEventScheduleError =
  | 'end_before_start'
  | 'min_duration'
  | 'max_duration'
  | 'start_too_soon';

export const getBroadcastEventDurationMs = (start: Date, end: Date): number =>
  end.getTime() - start.getTime();

export const validateBroadcastEventSchedule = (
  start: Date,
  end: Date,
  now = Date.now(),
): BroadcastEventScheduleError | null => {
  if (end.getTime() <= start.getTime()) { return 'end_before_start'; }
  if (start.getTime() < now + BROADCAST_LIVE_MIN_START_AHEAD_MS) { return 'start_too_soon'; }

  const durationMs = getBroadcastEventDurationMs(start, end);
  if (durationMs < BROADCAST_LIVE_EVENT_MIN_DURATION_MS) { return 'min_duration'; }
  if (durationMs > BROADCAST_LIVE_EVENT_MAX_DURATION_MS) { return 'max_duration'; }

  return null;
};


export const validateBroadcastEventDateSchedule = (
  startDate: Date,
  endDate: Date,
): BroadcastEventScheduleError | null => {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  // End date cannot be before start date
  if (end.getTime() < start.getTime()) {
    return 'end_before_start';
  }

  // Maximum allowed = next day
  const maxEndDate = new Date(start);
  maxEndDate.setDate(maxEndDate.getDate() + 1);

  if (end.getTime() > maxEndDate.getTime()) {
    return 'max_duration';
  }

  return null;
};

export const validateBroadcastEventTimeSchedule = (
  startDateTime: Date,
  endDateTime: Date,
): BroadcastEventScheduleError | null => {
  const durationMs = endDateTime.getTime() - startDateTime.getTime();

  if (durationMs < 0) {
    return 'end_before_start';
  }

  if (durationMs > BROADCAST_LIVE_EVENT_MAX_DURATION_MS) {
    return 'max_duration';
  }

  return null;
};

export const getMaxBroadcastEventEndTime = (start: Date): Date =>
  new Date(start.getTime() + BROADCAST_LIVE_EVENT_MAX_DURATION_MS);

type BroadcastStreamTiming = {
  blStartTime?: string | null;
  blEndTime?: string | null;
};

type BroadcastStreamStatus = BroadcastStreamTiming & {
  blStatus?: string | null;
  blIsLive?: boolean | null;
  blHlsPlayoutUrl?: string | null;
  blHlsPreviewPlayoutUrl?: string | null;
  blDashPlayoutUrl?: string | null;
  blDashPreviewPlayoutUrl?: string | null;
};

export const normalizeBroadcastStatus = (blStatus?: string | null): string => blStatus?.toLowerCase() ?? '';

const isStreamingStatus = (blStatus?: string | null): boolean => {
  const status = normalizeBroadcastStatus(blStatus);
  return status === 'streaming' || status === 'live' || status === 'preview' || status === 'started';
};

export const hasBroadcastPreviewSource = (stream: BroadcastStreamStatus): boolean =>
  !!(stream.blHlsPreviewPlayoutUrl || stream.blDashPreviewPlayoutUrl);

export const hasBroadcastPlayoutSource = (stream: BroadcastStreamStatus): boolean =>
  !!(stream.blHlsPlayoutUrl || stream.blDashPlayoutUrl);

/** JW live-stream VOD clips use `t=start-end` on the broadcast HLS URL (same as EPG catch-up). */
export const formatBroadcastTimeParam = (iso?: string | null): string => {
  if (!iso) { return ''; }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) { return ''; }
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

/** Build the HLS URL for an ended broadcast (DVR/VOD clip from the live stream). */
export const getBroadcastVodSourceUrl = (
  stream: BroadcastStreamTiming & {
    blStatus?: string | null;
    blHlsPlayoutUrl?: string | null;
    blDashPlayoutUrl?: string | null;
  },
): string => {
  const baseUrl = stream.blHlsPlayoutUrl || stream.blDashPlayoutUrl?.replace('.mpd', '.m3u8') || '';
  if (!baseUrl) { return ''; }

  const start = formatBroadcastTimeParam(stream.blStartTime);
  const status = normalizeBroadcastStatus(stream.blStatus);
  const endedEarly = status === 'captured'
  const endIso = endedEarly ? new Date().toISOString() : stream.blEndTime;
  const end = formatBroadcastTimeParam(endIso);
  if (!start || !end) { return baseUrl; }

  return createURL(baseUrl, { t: `${start}-${end}` });
};

/** Returns true when the scheduled end time has passed. */
export const isBroadcastLiveEnded = (blEndTime?: string | null): boolean => {
  if (!blEndTime) { return false; }
  const endMs = new Date(blEndTime).getTime();
  return !Number.isNaN(endMs) && endMs <= Date.now();
};

export const isBroadcastCreating = (stream: BroadcastStreamStatus): boolean =>
  normalizeBroadcastStatus(stream.blStatus) === 'creating';

export const isBroadcastReady = (stream: BroadcastStreamStatus): boolean =>
  normalizeBroadcastStatus(stream.blStatus) === 'ready';

export const isBroadcastStarting = (stream: BroadcastStreamStatus): boolean =>
  normalizeBroadcastStatus(stream.blStatus) === 'starting';

export const isBroadcastScheduled = (stream: BroadcastStreamStatus): boolean =>
  normalizeBroadcastStatus(stream.blStatus) === 'scheduled';

export const isBroadcastCompleted = (stream: BroadcastStreamStatus): boolean => {
  const status = normalizeBroadcastStatus(stream.blStatus);
  return status === 'completed' || isBroadcastLiveEnded(stream.blEndTime);
};

export const isBroadcastStreaming = (stream: BroadcastStreamStatus): boolean =>
  normalizeBroadcastStatus(stream.blStatus) === 'streaming';

export const isBroadcastStopping = (stream: BroadcastStreamStatus): boolean =>
  normalizeBroadcastStatus(stream.blStatus) === 'stopping';

export const isBroadcastStoppedEarly = (stream: BroadcastStreamStatus): boolean =>
  normalizeBroadcastStatus(stream.blStatus) === 'stopped' && !isBroadcastLiveEnded(stream.blEndTime);

/** Stream is actively broadcasting to the audience. */
export const isBroadcastCurrentlyLive = (stream: BroadcastStreamStatus): boolean => {
  if (isBroadcastLiveEnded(stream.blEndTime)) { return false; }
  if (stream.blIsLive === true) { return true; }

  const status = normalizeBroadcastStatus(stream.blStatus);

  if (status === 'streaming' && stream.blIsLive) {
    return hasBroadcastPlayoutSource(stream);
  }

  return false;
};

export const isBroadcastPlayable = (stream: BroadcastStreamStatus): boolean => {
  if (isBroadcastLiveEnded(stream.blEndTime)) { return false; }
  const status = normalizeBroadcastStatus(stream.blStatus);

  if (status === 'streaming' && stream.blIsLive) {
    return hasBroadcastPlayoutSource(stream);
  }

  return false;
}

/** Whether a broadcast list card should show the scheduled date/time tag. */
export const isBroadcastCardScheduled = (stream: BroadcastStreamStatus): boolean => {
  if (isBroadcastLiveEnded(stream.blEndTime)) { return false; }
  if (isBroadcastCurrentlyLive(stream)) { return false; }

  if (stream.blStartTime) { return true; }

  const status = normalizeBroadcastStatus(stream.blStatus);
  return status === 'scheduled' || status === 'ready' || status === 'creating' || status === 'starting';
};

export type BroadcastCardTagType = 'live' | 'scheduled';

/** Resolve list-card tag: LIVE badge or scheduled start time. */
export const getBroadcastCardTag = (
  stream: BroadcastStreamStatus,
): { type: BroadcastCardTagType; scheduledStart?: string } | null => {
  if (isBroadcastLiveEnded(stream.blEndTime)) { return null; }

  if (isBroadcastCurrentlyLive(stream)) {
    return { type: 'live' };
  }

  if (isBroadcastCardScheduled(stream)) {
    return { type: 'scheduled', scheduledStart: stream.blStartTime || undefined };
  }

  return null;
};

/** Format scheduled broadcast tag label, e.g. "Feb 13, 10:00 AM". */
export const formatBroadcastCardSchedule = (dateInput: Date | string, locale: string): string => {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  if (Number.isNaN(date.getTime())) { return ''; }

  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

/** Stream is in preview mode — encoder running, not yet live to audience (Figma "Started"). */
export const isBroadcastInPreview = (stream: BroadcastStreamStatus): boolean => {
  if (isBroadcastLiveEnded(stream.blEndTime)) { return false; }
  return isBroadcastReady(stream)
};

/** Whether the detail page should render the VOD player (completed / stopped / ended). */
export const shouldPlayBroadcastVod = (stream: BroadcastStreamStatus): boolean => {
  const vodSrc = getBroadcastVodSourceUrl(stream);
  if (!vodSrc) { return false; }

  const status = normalizeBroadcastStatus(stream.blStatus);
  if (status === 'stopped' || status === 'streaming') { return true; }
  return isBroadcastLiveEnded(stream.blEndTime);
};

/** Whether the detail page should render the HLS player for live or preview. */
export const shouldPlayBroadcastStream = (stream: BroadcastStreamStatus): boolean => {
  if (shouldPlayBroadcastVod(stream)) { return false; }
  if (isBroadcastLiveEnded(stream.blEndTime)) { return false; }
  if (isBroadcastCreating(stream) || isBroadcastReady(stream) || isBroadcastStarting(stream)) { return false; }
  if (isBroadcastScheduled(stream) && !isStreamingStatus(stream.blStatus)) { return false; }

  const src = getBroadcastHlsSource(stream);
  if (!src) { return false; }

  if (isBroadcastStopping(stream)) { return true; }
  if (stream.blIsLive === true) { return true; }
  return isBroadcastInPreview(stream);
};

export const createBroadcastPlayerItem = (
  stream: BroadcastStreamStatus & {
    blId?: string;
    blJwStreamId?: string;
    blTitle?: string | null;
    blDescription?: string | null;
  },
  src: string,
  mode: 'live' | 'vod' = 'live',
) => ({
  mediaid: stream.blJwStreamId || stream.blId || 'live-stream',
  title: stream.blTitle || '',
  description: stream.blDescription || '',
  file: src,
  sources: [{ file: src, type: 'hls' as const }],
  duration: 0,
  mediaStatus: mode === 'vod' ? ('VOD' as const) : ('LIVE' as const),
  ...(mode === 'live' ? { contentType: 'live' as const } : {}),
  allSources: undefined,
});

export const getBroadcastHlsSource = (
  stream: {
    blHlsPlayoutUrl?: string | null;
    blHlsPreviewPlayoutUrl?: string | null;
    blIsLive?: boolean | null;
  },
  preferVod = false,
): string => {
  if (preferVod) {
    return getBroadcastVodSourceUrl(stream);
  }

  if (stream.blIsLive === true) {
    return stream.blHlsPlayoutUrl || '';
  }

  if (stream.blHlsPreviewPlayoutUrl) {
    return stream.blHlsPreviewPlayoutUrl;
  }

  return stream.blHlsPlayoutUrl || '';
};
